import { Box, Heading, Text, HStack, Icon } from "@chakra-ui/react";
import { MdRestaurant as RestaurantIcon, MdArrowBack as BackIcon } from "react-icons/md";
import { useLocation } from "wouter";
import LegalSection from "@/components/LegalSection";
import LegalP from "@/components/LegalP";
import LegalLi from "@/components/LegalLi";

export default function Privacy() {
  const [, setLocation] = useLocation();

  return (
    <Box bg="bg.canvas" minH="100vh">
      {/* Header */}
      <Box
        as="header"
        bg="bg.surface"
        borderBottom="1px solid"
        borderColor="border.muted"
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Box h="env(safe-area-inset-top, 0px)" bg="accent.emphasis" />
        <Box
          px={{ base: 5, md: 8, lg: 12 }}
          py={3}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
        <HStack gap={2}>
          <Box
            bg="bg.surface"
            border="1px solid"
            borderColor="border.muted"
            borderRadius="7px"
            w="28px" h="28px"
            display="flex" alignItems="center" justifyContent="center"
            flexShrink={0}
          >
            <Icon as={RestaurantIcon} boxSize={4} color="accent.emphasis" />
          </Box>
          <Heading fontFamily="heading" fontWeight="800" fontSize="18px" color="text.primary">
            Byte <Text as="span" color="accent.emphasis">Pantry</Text>
          </Heading>
        </HStack>
        <Box
          as="button"
          display="flex"
          alignItems="center"
          gap={1}
          onClick={() => setLocation("/")}
          fontFamily="body"
          fontSize="13px"
          fontWeight="500"
          color="text.muted"
          cursor="pointer"
          _hover={{ color: "text.primary" }}
          border="none"
          bg="transparent"
          p={0}
        >
          <Icon as={BackIcon} boxSize={4} />
          Back
        </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box
        maxW={{ base: "100%", md: "680px" }}
        mx="auto"
        px={{ base: 5, md: 8 }}
        py={{ base: 10, md: 14 }}
      >
        <Heading
          fontFamily="heading"
          fontWeight="800"
          fontSize={{ base: "26px", md: "32px" }}
          color="text.primary"
          mb={2}
        >
          Privacy Policy
        </Heading>
        <Text fontFamily="body" fontWeight="300" fontSize="13px" color="text.muted" mb={10}>
          Last updated: March 2026
        </Text>

        <LegalSection title="Who we are">
          <LegalP>
            Byte Pantry is a recipe management app that helps you save, organise, and cook from
            your personal recipe collection. This policy explains what data Byte Pantry handles
            and how it is used.
          </LegalP>
          <LegalP>
            For questions about this policy, contact us at{" "}
            <Text as="span" color="accent.secondary" fontWeight="500">privacy@byte-pantry.com</Text>.
          </LegalP>
        </LegalSection>

        <LegalSection title="What data we hold">
          <LegalP>
            Byte Pantry does not directly store your personal data (such as your name, email address,
            or password). Authentication is handled entirely by{" "}
            <Text as="span" fontWeight="500">Auth0</Text> — we never see or store your credentials.
            Auth0 is the data controller for your account identity. See{" "}
            <Text as="span" color="accent.secondary" fontWeight="500">auth0.com/privacy</Text> for
            their policy.
          </LegalP>
          <LegalP>We do store the following on our servers:</LegalP>
          <LegalLi><strong>Recipe content</strong> — recipes you import, create, or edit, including titles, ingredients, instructions, images, and tags. This is linked to your Auth0 user ID.</LegalLi>
          <LegalLi><strong>Server logs</strong> — standard request logs (timestamps, endpoints, error codes) retained for up to 90 days for debugging.</LegalLi>
        </LegalSection>

        <LegalSection title="How we use it">
          <LegalP>Recipe content is used solely to provide the service to you:</LegalP>
          <LegalLi>To store and serve your recipe collection</LegalLi>
          <LegalLi>To process recipe imports (URL, image, text)</LegalLi>
          <LegalLi>To power AI features — recipe extraction and the AI Chef</LegalLi>
          <LegalP>We do not sell your data. We do not use your recipes to train AI models.</LegalP>
        </LegalSection>

        <LegalSection title="Third-party services">
          <LegalP>Byte Pantry relies on the following third parties:</LegalP>
          <LegalLi><strong>Auth0</strong> — the data controller for your account identity (email, login credentials). Any personal data requests should be directed to Auth0.</LegalLi>
          <LegalLi><strong>Amazon Web Services (AWS)</strong> — hosts the application and stores recipe images in S3, within the EU.</LegalLi>
          <LegalLi><strong>Anthropic / OpenAI</strong> — powers AI features. Recipe content may be sent to these APIs to fulfil your request. Neither provider is permitted to use this to train models under our API agreements.</LegalLi>
        </LegalSection>

        <LegalSection title="Deleting your account">
          <LegalP>
            To delete your account, email{" "}
            <Text as="span" color="accent.secondary" fontWeight="500">privacy@byte-pantry.com</Text>.
            We will permanently delete all your recipe content from our servers within 30 days,
            and submit a deletion request to Auth0 to remove your account identity.
          </LegalP>
        </LegalSection>

        <LegalSection title="Your rights">
          <LegalP>
            For personal data held by Auth0 (your email, login identity) — contact Auth0 directly
            or email us and we will facilitate the request on your behalf.
          </LegalP>
          <LegalP>
            For recipe content held by Byte Pantry — email{" "}
            <Text as="span" color="accent.secondary" fontWeight="500">privacy@byte-pantry.com</Text>{" "}
            to access, export, or delete your recipes. We will respond within 30 days.
          </LegalP>
        </LegalSection>

        <LegalSection title="Cookies">
          <LegalP>
            Byte Pantry uses strictly necessary cookies and local storage to keep you logged in and
            remember your preferences. We do not use advertising or tracking cookies.
          </LegalP>
        </LegalSection>

        <LegalSection title="Changes to this policy">
          <LegalP>
            We may update this policy from time to time. If we make material changes, we will notify
            you by email or with a notice in the app before the changes take effect.
          </LegalP>
        </LegalSection>

        <Box borderTop="1px solid" borderColor="border.muted" pt={8} mt={4}>
          <Text fontFamily="body" fontWeight="300" fontSize="12px" color="text.muted">
            &copy; 2026 Byte Pantry. All rights reserved.
          </Text>
        </Box>
      </Box>
    </Box>
  );
}
